/**
 * T-045 — pure helpers for the admin statistics dashboard.
 * Deterministic formatting (no Intl locale quirks) + client-side CSV builders.
 */

// Thousands-separated fixed-2 money, e.g. formatMoney(1234.5) -> "$1,234.50".
export const formatMoney = (n, symbol = '$') => {
  const v = Number(n) || 0;
  const [int, dec] = v.toFixed(2).split('.');
  const sign = int.startsWith('-') ? '-' : '';
  const digits = int.replace('-', '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${sign}${symbol}${digits}.${dec}`;
};

export const formatMXN = (n) => `${formatMoney(n)} MXN`;
export const formatUSD = (n) => `${formatMoney(n)} USD`;

// Thousands-separated integer, e.g. formatInt(12345) -> "12,345".
export const formatInt = (n) =>
  String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// OpenRouter reports pricing per TOKEN (USD strings, e.g. prompt "0.0000006").
// The pricing catalog stores cost per MILLION tokens, so multiply by 1e6.
// Returns null when neither prompt nor completion is a usable positive number
// (so the caller leaves the admin's manual values untouched).
export const mtokFromOpenRouter = (pricing) => {
  if (!pricing || typeof pricing !== 'object') return null;
  const p = Number(pricing.prompt);
  const c = Number(pricing.completion);
  const input = Number.isFinite(p) && p > 0 ? p * 1_000_000 : 0;
  const output = Number.isFinite(c) && c > 0 ? c * 1_000_000 : 0;
  if (input === 0 && output === 0) return null;
  return { input_cost_per_mtok: input, output_cost_per_mtok: output };
};

// RFC-4180-ish CSV: quote fields containing comma, quote or newline.
export const toCSV = (headers, rows) => {
  const esc = (v) => {
    let s = v == null ? '' : String(v);
    // Neutralize CSV/formula injection: a field starting with = + - @ (or a
    // leading tab/CR) is executed as a formula by Excel/Sheets on open.
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers, ...rows].map((row) => row.map(esc).join(',')).join('\n');
};

export const byDayToCSV = (byDay = []) =>
  toCSV(
    ['fecha', 'costo_usd', 'creditos_consumidos', 'tokens'],
    byDay.map((d) => [d.date, d.cost_usd, d.credits_consumed, d.tokens])
  );

export const byUserToCSV = (byUser = []) =>
  toCSV(
    ['user_id', 'email', 'creditos_consumidos', 'costo_usd'],
    byUser.map((u) => [u.user_id, u.email, u.credits_consumed, u.cost_usd])
  );

// Browser side effect: trigger a CSV file download. No-op outside the browser.
export const downloadCSV = (filename, csv) => {
  if (typeof document === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
